import React, { useRef } from 'react';
import { Button, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { AppDialog, KeyValueList, StatusChip } from '../ui';
import type { KeyValueItem } from '../ui/KeyValueList/KeyValueList';
import { formatDate } from '../../lib/formatDate';
import { MockupEvent, asFlag, isTestTemplate, mockupEventId, mockupStatus, safeHref, sortMockupTags } from '../../lib/mockupEvents';

const useStyles = createUseStyles((theme: Theme) => {
    const { color, radius, mq } = theme.custom;
    return {
        section: { margin: '20px 0 0', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.text.secondary },
        images: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginTop: 10, [mq.sm]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } },
        figure: { margin: 0, minWidth: 0 },
        caption: { display: 'block', fontSize: 12, color: color.text.secondary, marginBottom: 6 },
        imageLink: {
            display: 'block',
            aspectRatio: '1',
            borderRadius: radius.md,
            overflow: 'hidden',
            border: `1px solid ${color.border}`,
            backgroundColor: color.surfaceMuted,
            '&:focus-visible': { outline: `2px solid ${color.focusRing}`, outlineOffset: 2 },
        },
        image: { display: 'block', width: '100%', height: '100%', objectFit: 'contain' },
        tags: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 },
        legend: { margin: '8px 0 0', fontSize: 12, color: color.text.secondary },
        link: { color: color.primary.main, overflowWrap: 'anywhere' },
    };
});

export interface MockupEventDialogProps {
    event: MockupEvent | null;
    open: boolean;
    onClose: () => void;
    /** Shown as a secondary action when the dialog is not already inside the customer history. */
    onHistory?: (customerId: string | number) => void;
}

const yesNo = (value: unknown): string => (value === null || value === undefined ? '—' : asFlag(value) ? 'Yes' : 'No');

/** Every field of one upload event, its images and tags. */
const MockupEventDialog = ({ event: currentEvent, open, onClose, onHistory }: MockupEventDialogProps) => {
    const classes = useStyles();
    // Keep the last event while the dialog fades out, so the content does not blank.
    const lastEvent = useRef<MockupEvent | null>(null);
    if (currentEvent) lastEvent.current = currentEvent;
    const event = currentEvent ?? lastEvent.current;
    const id = mockupEventId(event);
    const status = mockupStatus(event?.status);
    const tags = sortMockupTags(event?.tags);
    const urlPath = safeHref(event?.urlPath);
    const items: KeyValueItem[] = event
        ? [
              { label: 'Event ID', value: id, mono: true },
              { label: 'Customer ID', value: event.customerId, mono: true },
              { label: 'Session ID', value: event.sessionId, mono: true },
              { label: 'Created', value: event.createdAt ? formatDate(event.createdAt, { time: true }) : null },
              { label: 'Status', value: <StatusChip tone={status.tone} size="sm">{status.label}</StatusChip> },
              { label: 'Image recognized', value: yesNo(event.imageRecognized) },
              ...(event.recognitionStatus != null ? [{ label: 'Recognition status', value: event.recognitionStatus }] : []),
              { label: 'Template', value: isTestTemplate(event) ? <StatusChip tone="danger" size="sm">A/B test</StatusChip> : event.template },
              { label: 'Product', value: event.product },
              { label: 'SKU', value: event.productSku, mono: true },
              { label: 'URL path', value: urlPath ? <a className={classes.link} href={urlPath} target="_blank" rel="noreferrer">{event.urlPath}</a> : event.urlPath },
              { label: 'Added to cart', value: asFlag(event.toCart) ? (event.toCartDate ? formatDate(event.toCartDate, { time: true }) : 'Yes') : yesNo(event.toCart) },
              { label: 'Ordered', value: yesNo(event.orderExist) },
              { label: 'IP', value: event.ip, mono: true },
              { label: 'Generation type', value: event.generationTypeId },
              { label: 'Type ID', value: event.typeId },
              { label: 'More uploads', value: yesNo(event.additionalEvents) },
              ...(event.errorMessage ? [{ label: 'Error', value: event.errorMessage }] : []),
          ]
        : [];
    const images = [
        { label: 'Original', url: safeHref(event?.imageUrl2) },
        { label: 'Uploaded photo', url: safeHref(event?.imageUrl) },
        { label: 'Mockup', url: safeHref(event?.mockupUrl) },
    ].filter((image) => image.url);
    const visibleCount = tags.filter((tag) => asFlag(tag.visible)).length;

    return (
        <AppDialog
            open={open && !!currentEvent}
            onClose={onClose}
            title={id != null ? `Upload ${id}` : 'Upload'}
            maxWidth="md"
            actions={(
                <>
                    {onHistory && event?.customerId != null && (
                        <Button variant="outlined" onClick={() => onHistory(event.customerId as string | number)}>Customer history</Button>
                    )}
                    <Button variant="contained" onClick={onClose}>Close</Button>
                </>
            )}
        >
            <KeyValueList items={items} minColumnWidth={170} />
            {images.length > 0 && (
                <>
                    <h3 className={classes.section}>Images</h3>
                    <div className={classes.images}>
                        {images.map((image) => (
                            <figure key={image.label} className={classes.figure}>
                                <figcaption className={classes.caption}>{image.label}</figcaption>
                                <a className={classes.imageLink} href={image.url as string} target="_blank" rel="noreferrer" aria-label={`Open ${image.label.toLowerCase()} in a new tab`}>
                                    <img className={classes.image} src={image.url as string} alt="" loading="lazy" />
                                </a>
                            </figure>
                        ))}
                    </div>
                </>
            )}
            {tags.length > 0 && (
                <>
                    <h3 className={classes.section}>Tags</h3>
                    <div className={classes.tags}>
                        {tags.map((tag, index) => (
                            <StatusChip key={`${tag.tagId}-${index}`} size="sm" tone={asFlag(tag.visible) ? 'success' : 'neutral'} title={asFlag(tag.visible) ? 'Shown to the customer' : 'Hidden from the customer'}>
                                {tag.tagName}
                            </StatusChip>
                        ))}
                    </div>
                    <p className={classes.legend}>{visibleCount} of {tags.length} tags are shown to the customer; grey tags are internal.</p>
                </>
            )}
        </AppDialog>
    );
};

export default MockupEventDialog;
